// app/utils/agentviewcontext.tsx
'use client';

import React, { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type AgentViewMode = 'menu' | 'list' | 'form' | 'global';

export type AgentViewContextValue = {
  source: 'spreadtable' | 'global';
  spread_id?: string | null;
  display?: string | null;
  mode?: AgentViewMode | null;
  pkey?: string | null;
  form_pkey?: string | null;
  selected_record?: {
    mode: 'form';
    source: 'selected_from_list' | 'direct_form';
    pkey_name?: string | null;
    pkey_value?: string | null;
    row_index?: number | null;
    visible_values?: Record<string, any>;
  } | null;
  base_filters?: Record<string, any> | null;
  ui_filters?: Record<string, any> | null;
  current_path?: string | null;
};

type AgentViewContextState = {
  agentViewContext: AgentViewContextValue | null;
  setAgentViewContext: (context: AgentViewContextValue | null, ownerId?: string) => void;
  clearAgentViewContext: (ownerId?: string) => void;
};

const AgentViewContext = createContext<AgentViewContextState | undefined>(undefined);

export const AgentViewProvider = ({ children }: { children: ReactNode }) => {
  const [agentViewContext, setAgentViewContext] = useState<AgentViewContextValue | null>(null);
  const contextStackRef = useRef<Map<string, AgentViewContextValue>>(new Map());
  const setOwnedAgentViewContext = useCallback((context: AgentViewContextValue | null, ownerId = 'default') => {
    if (context) {
      contextStackRef.current.delete(ownerId);
      contextStackRef.current.set(ownerId, context);
      setAgentViewContext(context);
      return;
    }
    contextStackRef.current.delete(ownerId);
    const contexts = Array.from(contextStackRef.current.values());
    setAgentViewContext(contexts.length > 0 ? contexts[contexts.length - 1] : null);
  }, []);
  const clearAgentViewContext = useCallback((ownerId = 'default') => {
    contextStackRef.current.delete(ownerId);
    const contexts = Array.from(contextStackRef.current.values());
    setAgentViewContext(contexts.length > 0 ? contexts[contexts.length - 1] : null);
  }, []);
  const value = useMemo(
    () => ({ agentViewContext, setAgentViewContext: setOwnedAgentViewContext, clearAgentViewContext }),
    [agentViewContext, setOwnedAgentViewContext, clearAgentViewContext]
  );

  return (
    <AgentViewContext.Provider value={value}>
      {children}
    </AgentViewContext.Provider>
  );
};

export const useAgentView = () => {
  const ctx = useContext(AgentViewContext);
  if (!ctx) throw new Error('useAgentView must be used within AgentViewProvider');
  return ctx;
};
